/**
 * Textarea component — Brand Book §2.3
 * Matches Input styling: bg-muted, 0px radius, orange focus ring at 8%
 * Orange dot (•) for required, error/hint below
 */

import { forwardRef, type TextareaHTMLAttributes } from 'react';
import { clsx } from 'clsx';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
  resize?: 'none' | 'vertical' | 'both';
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, hint, resize = 'vertical', className, id, ...props }, ref) => {
    const textareaId = id || label?.toLowerCase().replace(/\s+/g, '-');

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={textareaId}
            className="block text-body-sm font-semibold text-txt-secondary mb-1"
          >
            {label}
            {props.required && (
              <span className="text-orange ml-1">•</span>
            )}
          </label>
        )}
        <textarea
          ref={ref}
          id={textareaId}
          rows={props.rows ?? 3}
          className={clsx(
            'w-full px-3 py-2 border bg-surface-muted text-txt-primary',
            'placeholder:text-txt-tertiary',
            'font-sans text-body font-normal',
            'transition-[border-color,box-shadow] duration-200 ease',
            'hover:border-border-strong',
            'focus:outline-none focus:border-orange focus:shadow-[0_0_0_2px_var(--orange-light)]',
            'disabled:opacity-40 disabled:cursor-not-allowed',
            error
              ? 'border-error shadow-[0_0_0_2px_var(--error-light)]'
              : 'border-border',
            resize === 'none' && 'resize-none',
            resize === 'vertical' && 'resize-y',
            resize === 'both' && 'resize',
            className,
          )}
          {...props}
        />
        {error && (
          <p className="mt-1 text-caption text-error">{error}</p>
        )}
        {hint && !error && (
          <p className="mt-1 text-caption text-txt-tertiary">{hint}</p>
        )}
      </div>
    );
  },
);

Textarea.displayName = 'Textarea';
